import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import * as XLSX from 'xlsx';
import useUser from '../../hooks/useUser';
import { getSalesReactBaseUrl } from '../../utilities/api';
import '../CategoryMaster.css';

const API = `${getSalesReactBaseUrl()}/api/server_masters/`;
const PRODUCT_PAGE_SIZE = 500; // backend caps limit at 500 — use the max to minimize round trips
const MAX_PRODUCT_PAGES = 50; // safety cap (25,000 products) in case `hasMore` is ever wrong

/* ================================================================ */
/* OPTION CONSTANTS                                                 */
/* ================================================================ */
const DISCOUNT_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 40, 50].map((v) => ({ label: `${v}%`, value: v }));
const STATUS_OPTIONS = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' }
];

const TAX_OPTIONS = [
    { label: '+0 %  (Forward)', value: '+0' },
    { label: '+5 %  (Forward)', value: '+5' },
    { label: '+12 % (Forward)', value: '+12' },
    { label: '+18 % (Forward)', value: '+18' },
    { label: '+28 % (Forward)', value: '+28' },
    { label: '-0 %  (Reverse)', value: '-0' },
    { label: '-5 %  (Reverse)', value: '-5' },
    { label: '-12 % (Reverse)', value: '-12' },
    { label: '-18 % (Reverse)', value: '-18' },
    { label: '-28 % (Reverse)', value: '-28' }
];

const PRODUCT_TYPE_OPTIONS = [
    { label: 'VEG', value: 'VEG' },
    { label: 'NONVEG', value: 'NONVEG' },
    { label: 'EGG', value: 'EGG' }
];

const SALES_ITEM_TYPE_OPTIONS = [
    { label: 'Paid', value: 'paid' },
    { label: 'Free', value: 'free' },
    { label: 'Complimentary', value: 'complimentary' }
];

/* ================================================================ */
/* HELPER FUNCTIONS                                                 */
/* ================================================================ */
const toNum = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};
const round = (v, d = 3) => {
    const f = Math.pow(10, d);
    return Math.round(toNum(v) * f) / f;
};
const fmtDMY = (isoOrDate) => {
    if (!isoOrDate) return '';
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(String(isoOrDate).replace(' ', 'T'));
    if (isNaN(d.getTime())) return String(isoOrDate);
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};
const toISODate = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const parseISODate = (s) => {
    if (!s) return null;
    if (s instanceof Date) return s;
    const d = new Date(String(s).replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
};

/* ================================================================ */
/* TAX CALCULATION                                                  */
/* ================================================================ */
const calcTax = (inputValue, taxStr) => {
    const raw = String(taxStr ?? '+0');
    const isReverse = raw.startsWith('-');
    const t = Math.abs(parseFloat(raw)) || 0;
    const v = toNum(inputValue);
    let basic, selling, taxAmount;
    if (isReverse) {
        selling = v;
        basic = t === 0 ? v : round(v / (1 + t / 100), 3);
        taxAmount = round(selling - basic, 3);
    } else {
        basic = v;
        taxAmount = round((basic * t) / 100, 3);
        selling = round(basic + taxAmount, 2);
    }
    return {
        basic_rate: round(basic, 3),
        price: round(selling, 2),
        basic_tax_amount: taxAmount,
        sgst_tax: round(t / 2, 3),
        cgst_tax: round(t / 2, 3),
        sgst_tax_amount: round(taxAmount / 2, 3),
        cgst_tax_amount: round(taxAmount / 2, 3),
        is_reverse: isReverse
    };
};

/* ================================================================ */
/* BLANK FORM                                                        */
/* ================================================================ */
const BLANK_FORM = {
    product_name: '',
    hsn_code: '0',
    product_no: '0',
    product_code: '0',
    sorting_order: '0',
    uom: 'Nos',
    basic_rate: 0,
    basic_tax_percent: '+5',
    basic_tax_amount: 0,
    sgst_tax: 2.5,
    sgst_tax_amount: 0,
    cgst_tax: 2.5,
    cgst_tax_amount: 0,
    price: 0,
    discount_perc: null,
    product_stock: '0',
    token: '0',
    tokengroup: '0',
    sales_item_type: 'paid',
    product_type: 'VEG',
    product_image: '0',
    product_image_path: '0',
    barcode_number: '0',
    effective_from: toISODate(new Date()),
    data_transfer: 'pending',
    product_status: 'Active',
    category_master_id: null,
    branch_id: null,
    client_id: null,
    platform_id: null,
    prcat_cat_id: null,
    section_id: null
};

const REQUIRED_FIELDS = ['product_name', 'uom', 'basic_tax_percent', 'price', 'effective_from', 'product_status', 'client_id', 'branch_id', 'category_master_id', 'prcat_cat_id', 'platform_id'];
const FIELD_LABELS = {
    product_name: 'Product Name', uom: 'UOM', basic_tax_percent: 'Tax %', price: 'Selling Price',
    effective_from: 'Effective From', product_status: 'Status', client_id: 'Client', branch_id: 'Branch',
    category_master_id: 'Category Master', prcat_cat_id: 'Product Category', platform_id: 'Platform'
};

/* ================================================================ */
/* FIELD COMPONENT                                                  */
/* ================================================================ */
const Field = ({ label, required, error, children }) => (
    <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
            {label}
            {required && <span style={{ color: '#EF4444', marginLeft: '0.25rem' }}>*</span>}
        </label>
        {children}
        {error && <span style={{ color: '#EF4444', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{error}</span>}
    </div>
);

/* ================================================================ */
/* MAIN COMPONENT                                                   */
/* ================================================================ */
const ProductList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const actionsMenu = useRef(null);
    const originalEffRef = useRef(null);

    const { userRole, userClient } = useUser();
    const role = userRole || localStorage.getItem('role') || '';
    const roleLower = role?.toLowerCase() || '';
    const isGspos = roleLower === 'gspos';
    const lockedClientId = userClient || localStorage.getItem('clientid') || '';

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [categories, setCategories] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [sections, setSections] = useState([]);
    const [catMasters, setCatMasters] = useState([]);
    const [products, setProducts] = useState([]);

    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [statusFilter, setStatusFilter] = useState('active');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [statusCounts, setStatusCounts] = useState({ all: 0, active: 0, inactive: 0 });
    const [selectedRows, setSelectedRows] = useState([]);

    const [editModal, setEditModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState(BLANK_FORM);
    const [errors, setErrors] = useState({});

    const [tokenModal, setTokenModal] = useState(false);
    const [tokenValue, setTokenValue] = useState('');
    const [tokenEff, setTokenEff] = useState(toISODate(new Date()));
    const [tokenConfirm, setTokenConfirm] = useState(false);

    const [statusModal, setStatusModal] = useState(false);
    const [statusAction, setStatusAction] = useState('');
    const [statusEff, setStatusEff] = useState(toISODate(new Date()));

    const [discModal, setDiscModal] = useState(false);
    const [discValue, setDiscValue] = useState(0);
    const [discEff, setDiscEff] = useState(toISODate(new Date()));
    const [discConfirm, setDiscConfirm] = useState(false);

    const [priceModal, setPriceModal] = useState(false);
    const [priceRows, setPriceRows] = useState([]);
    const [priceEff, setPriceEff] = useState(toISODate(new Date()));
    const [priceConfirm, setPriceConfirm] = useState(false);

    const [importModal, setImportModal] = useState(false);
    const [savingImport, setSavingImport] = useState(false);
    const [importForm, setImportForm] = useState({ client: null, branch: null, catMaster: null, category: null, platform: null, section: null });
    const [importRows, setImportRows] = useState([]);

    /* ============================================================ */
    /* DATA LOADING                                                 */
    /* ============================================================ */
    const loadData = async () => {
        setLoading(true);
        try {
            const rolename = localStorage.getItem('role');
            const clientname = localStorage.getItem('clientid');
            // Client/Branch/Category dropdowns are sent as real server-side filters
            // (backend already supports them) instead of pulling the whole catalog
            // and filtering it in the browser.
            const branch_id = selectedBranch?.branch_id || '';
            const prcat_cat_id = selectedCategory?.prcat_cat_id || '';
            const client_id = isGspos && selectedClient ? selectedClient.client_id : '';
            // Active/Inactive/Total tiles and the search box are also real server-side
            // filters now, instead of narrowing an already-fetched page in the browser.
            const product_status = statusFilter === 'active' ? 'Active' : statusFilter === 'inactive' ? 'Inactive' : '';
            const search = globalFilterValue.trim();

            // Default (no filter picked / Reset clicked) view is scoped to pending-sync
            // records only, same as the original behaviour. Once a Branch/Category/Client
            // filter is actively picked, widen to every status so the filtered result is
            // complete (and doesn't hide just-saved edits within that scope).
            const hasActiveFilter = Boolean(branch_id || prcat_cat_id || client_id);
            const data_transfer = hasActiveFilter ? 'all' : 'pending';

            // Products are paginated server-side — a client with a large catalog
            // (hundreds+ products) would otherwise have to be returned in one
            // unbounded request, which was slow enough to time out. Page 1 tells us
            // total_records, so the remaining pages (if any) are known up front and
            // fetched in parallel instead of one-at-a-time — for a 10-page catalog
            // that's ~10x fewer sequential network round trips.
            const reqBase = { rolename, clientname, limit: PRODUCT_PAGE_SIZE, data_transfer, branch_id, prcat_cat_id, client_id, product_status, search };
            const firstResponse = (await axios.post(`${API}get_productlist`, { ...reqBase, page: 1 })).data;
            setProducts(firstResponse.products || []); // show the first page immediately

            const totalPages = Math.min(MAX_PRODUCT_PAGES, Math.ceil((firstResponse.total_records || 0) / PRODUCT_PAGE_SIZE));
            let allProducts = firstResponse.products || [];
            if (totalPages > 1) {
                const restPages = await Promise.all(
                    Array.from({ length: totalPages - 1 }, (_, i) => axios.post(`${API}get_productlist`, { ...reqBase, page: i + 2 }))
                );
                allProducts = allProducts.concat(...restPages.map((r) => r.data.products || []));
                setProducts(allProducts);
            }

            setClients(firstResponse.clients || []);
            setBranches(firstResponse.branches || []);
            setCategories(firstResponse.product_category || []);
            setPlatforms(firstResponse.platform || []);
            setSections(firstResponse.section || []);
            setCatMasters(firstResponse.category_master || []);
            if (firstResponse.status_counts) setStatusCounts(firstResponse.status_counts);
        } catch (error) {
            const detail = error?.response ? `Server responded with ${error.response.status}` : error?.code === 'ECONNABORTED' ? 'Request timed out' : 'Network error — please retry';
            toast.current?.show({ severity: 'error', summary: 'Could Not Load Products', detail });
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch from the server whenever any filter changes (dropdowns, status tiles,
    // or the search box), instead of filtering an already-fetched page in the browser.
    // Debounced so typing in the search box doesn't fire a request per keystroke.
    useEffect(() => {
        const t = setTimeout(() => { loadData(); }, 350);
        return () => clearTimeout(t);
    }, [selectedClient, selectedBranch, selectedCategory, statusFilter, globalFilterValue]);

    // Non-gspos roles don't get a client picker — lock onto their own client as
    // soon as the clients list (and therefore the matching client object) is available.
    useEffect(() => {
        if (isGspos || selectedClient || !clients.length) return;
        const ownClientId = userClient || localStorage.getItem('clientid') || '';
        const ownClient = clients.find((c) => String(c.client_id) === String(ownClientId));
        if (ownClient) setSelectedClient(ownClient);
    }, [isGspos, clients, selectedClient, userClient]);

    /* ============================================================ */
    /* FILTER CHAINING                                              */
    /* ============================================================ */
    const filteredBranches = useMemo(() => {
        if (!selectedClient) return [];
        return branches.filter((b) => String(b.client_id) === String(selectedClient.client_id));
    }, [selectedClient, branches]);

    const filteredCategories = useMemo(() => {
        if (!selectedBranch) return [];
        return categories.filter((c) => String(c.branch_id) === String(selectedBranch.branch_id));
    }, [selectedBranch, categories]);

    useEffect(() => {
        setSelectedBranch(null);
        setSelectedCategory(null);
    }, [selectedClient]);
    useEffect(() => {
        setSelectedCategory(null);
    }, [selectedBranch]);

    /* ============================================================ */
    /* FILTERED ROWS                                                */
    /* ============================================================ */
    // Every filter (Client/Branch/Category/status tiles/search) is now applied
    // server-side (see loadData) — products in state already match the selected scope.
    const displayedProducts = products;

    // Sourced from the server's status_counts (same scope, ignoring product_status)
    // so all three tiles stay visible/accurate regardless of which one is selected.
    const totalRecords = statusCounts.all;
    const activeCount = statusCounts.active;
    const inactiveCount = statusCounts.inactive;
    const pendingCount = products.filter((p) => String(p.data_transfer || '').toLowerCase() === 'pending').length;

    /* ============================================================ */
    /* UI HANDLERS                                                  */
    /* ============================================================ */
    const handleResetFilters = () => {
        setSelectedClient(null);
        setSelectedBranch(null);
        setSelectedCategory(null);
        setGlobalFilterValue('');
        setStatusFilter('active');
    };

    /* ============================================================ */
    /* FORM OPERATIONS                                              */
    /* ============================================================ */
    const setFormField = (patch) => {
        setForm((prev) => {
            const next = { ...prev, ...patch };
            const driverChanged = 'basic_tax_percent' in patch || 'basic_rate' in patch || 'price' in patch;
            if (driverChanged) {
                const isReverse = String(next.basic_tax_percent || '+0').startsWith('-');
                const driver = isReverse ? next.price : next.basic_rate;
                const calc = calcTax(driver, next.basic_tax_percent);
                next.basic_rate = calc.basic_rate;
                next.price = calc.price;
                next.basic_tax_amount = calc.basic_tax_amount;
                next.sgst_tax = calc.sgst_tax;
                next.sgst_tax_amount = calc.sgst_tax_amount;
                next.cgst_tax = calc.cgst_tax;
                next.cgst_tax_amount = calc.cgst_tax_amount;
            }
            if (isEditing) {
                const changed = Object.keys(patch).some((k) => k !== 'data_transfer' && String(patch[k]) !== String(prev[k]));
                if (changed) next.data_transfer = 'pending';
            }
            return next;
        });
    };

    const openAdd = () => {
        setIsEditing(false);
        originalEffRef.current = toISODate(new Date());
        // No platform is literally named "Counter" in the master data — POS (point of
        // sale / counter sales) is the agreed default. Section uses substring matching
        // since the master data has "Ac-Section" / "Non-Ac Section", not plain "AC".
        const defaultPlatform = platforms.find(
            (p) =>
                String(p.platform_name || '')
                    .trim()
                    .toLowerCase() === 'pos'
        );
        const acSection = sections.find(
            (s) =>
                String(s.section_name || '')
                    .toLowerCase()
                    .includes('ac') &&
                !String(s.section_name || '')
                    .toLowerCase()
                    .includes('non-ac')
        );
        setForm({
            ...BLANK_FORM,
            effective_from: originalEffRef.current,
            data_transfer: 'added',
            client_id: isGspos ? null : lockedClientId,
            platform_id: defaultPlatform ? defaultPlatform.platform_id : null,
            section_id: acSection ? acSection.sectionId || acSection.section_id : null
        });
        setErrors({});
        setEditModal(true);
    };

    const openEdit = (row) => {
        setIsEditing(true);
        const eff = row.effective_from || toISODate(new Date());
        originalEffRef.current = eff;
        // basic_tax_percent is stored as a plain positive number — direction lives in
        // its own tax_direction column. Fall back to the sign on basic_tax_percent
        // only for legacy rows saved before tax_direction existed.
        const rawTax = row.basic_tax_percent;
        const legacySigned = typeof rawTax === 'string' && (rawTax.startsWith('+') || rawTax.startsWith('-'));
        const isReverse = row.tax_direction ? row.tax_direction === 'reverse' : legacySigned && rawTax.startsWith('-');
        const rawTaxStr = `${isReverse ? '-' : '+'}${toNum(rawTax)}`;
        setForm({
            ...BLANK_FORM,
            ...row,
            basic_rate: toNum(row.basic_rate),
            basic_tax_percent: rawTaxStr,
            basic_tax_amount: toNum(row.basic_tax_amount),
            sgst_tax: toNum(row.sgst_tax),
            sgst_tax_amount: toNum(row.sgst_tax_amount),
            cgst_tax: toNum(row.cgst_tax),
            cgst_tax_amount: toNum(row.cgst_tax_amount),
            price: toNum(row.price),
            effective_from: eff,
            data_transfer: 'updated'
        });
        setErrors({});
        setEditModal(true);
    };

    const saveProduct = async () => {
        const errs = {};
        REQUIRED_FIELDS.forEach((k) => {
            const v = form[k];
            if (v === null || v === undefined || v === '') errs[k] = 'Required';
        });
        if (Object.keys(errs).length) {
            setErrors(errs);
            const missing = Object.keys(errs).map((k) => FIELD_LABELS[k] || k).join(', ');
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: `Missing required: ${missing}`, life: 6000 });
            return;
        }
        setSaving(true);
        try {
            const raw = String(form.basic_tax_percent || '+0');
            const payload = { ...form, basic_tax_percent: Math.abs(parseFloat(raw)) || 0, tax_direction: raw.startsWith('-') ? 'reverse' : 'forward' };
            if (isEditing) {
                await axios.post(`${API}update_product/${form.pr_id}`, payload);
                toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Product updated' });
            } else {
                await axios.post(`${API}save_product`, payload);
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Product created' });
            }
            setEditModal(false);
            await loadData();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Save failed' });
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================ */
    /* BULK OPERATIONS                                              */
    /* ============================================================ */
    const confirmStatusUpdate = (newStatus) => {
        if (!selectedRows || !selectedRows.length) {
            toast.current?.show({ severity: 'warn', summary: 'Selection Required', detail: 'Please select items using checkboxes' });
            return;
        }
        confirmDialog({
            message: `Are you sure you want to mark ${selectedRows.length} item(s) as ${newStatus}?`,
            header: 'Confirm Status Update',
            icon: 'pi pi-exclamation-triangle',
            accept: () => updateBulkStatus(newStatus),
            reject: () => toast.current?.show({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled' }),
            acceptLabel: 'Yes, Proceed',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text'
        });
    };

    const updateBulkStatus = async (newStatus) => {
        setLoading(true);
        try {
            const response = await fetch(`${API}bulk_status_update/product`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pr_ids: selectedRows.map((r) => String(r.pr_id)), status: newStatus, effective_from: toISODate(new Date()), data_transfer: 'pending' })
            });
            const result = await response.json();
            if (result.status === 'success' || result) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Status updated successfully' });
                setSelectedRows([]);
                await loadData();
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update status' });
        } finally {
            setLoading(false);
        }
    };

    const promoteToLive = async () => {
        const pendingRows = selectedRows.filter((s) => String(s.data_transfer || '').toLowerCase() === 'pending');
        if (!pendingRows.length) {
            toast.current?.show({ severity: 'warn', summary: 'No pending rows', detail: 'Select at least one row whose transfer status is pending' });
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}bulk_promote`, { pr_ids: pendingRows.map((s) => String(s.pr_id)) });
            toast.current?.show({ severity: 'success', summary: 'Promoted', detail: `${pendingRows.length} row(s) promoted to live` });
            setSelectedRows([]);
            await loadData();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Promote failed' });
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================ */
    /* IMPORT OPERATIONS                                            */
    /* ============================================================ */
    const importBranches = useMemo(() => {
        if (!importForm.client) return [];
        return branches.filter((b) => String(b.client_id) === String(importForm.client.client_id));
    }, [importForm.client, branches]);

    const importCatMasters = useMemo(() => {
        if (!importForm.client) return [];
        return catMasters.filter((c) => {
            const clientMatch = !c.client_id || String(c.client_id) === String(importForm.client.client_id);
            const branchMatch = !c.branch_id || (importForm.branch && String(c.branch_id) === String(importForm.branch.branch_id));
            return clientMatch && branchMatch;
        });
    }, [importForm.client, importForm.branch, catMasters]);

    const importCategories = useMemo(() => {
        if (!importForm.catMaster) return importForm.branch ? categories.filter((c) => String(c.branch_id) === String(importForm.branch.branch_id)) : [];
        return categories.filter((c) => String(c.category_master_id || c.catmaster_id || c.cat_master_id) === String(importForm.catMaster.category_master_id));
    }, [importForm.catMaster, importForm.branch, categories]);

    const mBranches = useMemo(() => (form.client_id ? branches.filter((b) => String(b.client_id) === String(form.client_id)) : []), [form.client_id, branches]);
    const mCatMasters = useMemo(() => {
        let list = catMasters;
        if (form.client_id) list = list.filter((c) => !c.client_id || String(c.client_id) === String(form.client_id));
        if (form.branch_id) list = list.filter((c) => !c.branch_id || String(c.branch_id) === String(form.branch_id));
        return list;
    }, [form.client_id, form.branch_id, catMasters]);
    // Product Category is no longer a visible field — it's auto-matched (by name) from
    // the selected Category Master in the dropdown's onChange below.
    const categoriesForBranch = useMemo(() => (form.branch_id ? categories.filter((c) => String(c.branch_id) === String(form.branch_id)) : []), [form.branch_id, categories]);

    const resetImportForm = () => {
        setImportForm({ client: null, branch: null, catMaster: null, category: null, platform: null, section: null });
        setImportRows([]);
        setSavingImport(false);
    };

    const handleImportFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        const ext = file.name.split('.').pop().toLowerCase();
        reader.onload = (evt) => {
            const wb = XLSX.read(evt.target.result, { type: ext === 'csv' ? 'binary' : 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws);
            setImportRows(data);
            toast.current?.show({ severity: 'info', summary: 'File Loaded', detail: `${data.length} rows ready to import` });
        };
        ext === 'csv' ? reader.readAsBinaryString(file) : reader.readAsArrayBuffer(file);
    };

    const handleImportPaste = (e) => {
        const text = e.clipboardData.getData('text/plain');
        const lines = text.trim().split(/\r?\n/);
        const rows = lines.map((l) => l.split(/\t|,/));
        const data = rows.map((c) => ({
            product_name: c[0]?.trim(),
            hsn_code: c[1],
            product_code: c[2],
            uom: c[3],
            basic_rate: Number(c[4]) || 0,
            tax_percentage: Number(c[5]) || 0,
            basic_tax_amount: Number(c[6]) || 0,
            cgst_tax: Number(c[7]) || 0,
            basic_cgst_amount: Number(c[8]) || 0,
            sgst_tax: Number(c[9]) || 0,
            basic_sgst_amount: Number(c[10]) || 0,
            selling_price: Number(c[11]) || 0,
            sales_item_type: c[12],
            product_type: c[13],
            token: Number(c[14]) || 0,
            tokengroup: Number(c[15]) || 0
        }));
        const cleaned = data.filter((r) => r.product_name);
        const names = cleaned.map((r) => r.product_name.toLowerCase());
        const duplicates = names.filter((n, i) => names.indexOf(n) !== i);
        if (duplicates.length) toast.current?.show({ severity: 'warn', summary: 'Duplicate Products Found', detail: [...new Set(duplicates)].join(', ') });
        setImportRows(cleaned);
    };

    const handleImportSubmit = async () => {
        if (!importForm.client || !importForm.catMaster || !importForm.category || !importForm.platform) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fill all required mappings correctly.' });
            return;
        }
        if (!importRows.length) {
            toast.current?.show({ severity: 'warn', summary: 'No Data', detail: 'Paste or upload product details first.' });
            return;
        }
        const names = importRows.map((r) => (r.product_name || '').toLowerCase().trim());
        const dups = names.filter((n, i) => names.indexOf(n) !== i);
        if (dups.length) {
            toast.current?.show({ severity: 'warn', summary: 'Duplicate Product Names', detail: [...new Set(dups)].join(', ') });
            return;
        }
        setSavingImport(true);
        try {
            const payload = {
                client_id: importForm.client.client_id,
                branch_id: importForm.branch?.branch_id || null,
                categorymasterid: importForm.catMaster.category_master_id,
                prcat_cat_id: importForm.category,
                platform_id: importForm.platform.platform_id,
                section_id: importForm.section?.section_id || null,
                items: importRows
            };
            const res = await axios.post(`${API}bulk_import_products`, payload);
            toast.current?.show({ severity: 'success', summary: 'Import Completed', detail: `✅ ${res.data.inserted || 0} new · 🔁 ${res.data.updated || 0} updated` });
            resetImportForm();
            setImportModal(false);
            await loadData();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Import Failed', detail: 'Please verify file or pasted data.' });
        } finally {
            setSavingImport(false);
        }
    };

    const applyBulkToken = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}bulk_token`, { pr_ids: selectedRows.map((s) => String(s.pr_id)), tokengroup: tokenValue.trim(), effective_from: tokenEff, data_transfer: 'pending' });
            toast.current?.show({ severity: 'success', summary: 'Token Updated', detail: `${selectedRows.length} rows updated` });
            setTokenModal(false);
            setTokenConfirm(false);
            setTokenValue('');
            setTokenEff(toISODate(new Date()));
            setSelectedRows([]);
            await loadData();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed' });
        } finally {
            setSaving(false);
        }
    };

    const applyBulkDiscount = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}bulk_discount`, { pr_ids: selectedRows.map((s) => String(s.pr_id)), discount_perc: Number(discValue), effective_from: discEff, data_transfer: 'pending' });
            toast.current?.show({ severity: 'success', summary: 'Discount Updated', detail: `${discValue}% applied to ${selectedRows.length} rows` });
            setDiscModal(false);
            setDiscConfirm(false);
            setDiscEff(toISODate(new Date()));
            setSelectedRows([]);
            await loadData();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed' });
        } finally {
            setSaving(false);
        }
    };

    const openPriceChange = () => {
        if (!selectedRows.length) {
            toast.current?.show({ severity: 'warn', summary: 'No rows', detail: 'Select at least one product row' });
            return;
        }
        const clientIds = [...new Set(selectedRows.map((s) => String(s.client_id)))];
        if (clientIds.length > 1) {
            toast.current?.show({ severity: 'error', summary: 'Multiple clients', detail: 'Price change allowed only for items of the same client' });
            return;
        }
        const rowsInit = selectedRows.map((s) => {
            const basic = toNum(s.basic_rate);
            const taxPct = `${s.tax_direction === 'reverse' ? '-' : '+'}${toNum(s.basic_tax_percent)}`;
            const calc = calcTax(basic, taxPct);
            return {
                pr_id: s.pr_id,
                branch_name: s.branch_name,
                product_name: s.product_name,
                basic_price: calc.basic_rate,
                tax_percent: taxPct,
                tax_amount: calc.basic_tax_amount,
                sgst_tax: calc.sgst_tax,
                sgst_amount: calc.sgst_tax_amount,
                cgst_tax: calc.cgst_tax,
                cgst_amount: calc.cgst_tax_amount,
                total_selling_price: calc.price
            };
        });
        setPriceRows(rowsInit);
        setPriceEff(toISODate(new Date()));
        setPriceConfirm(false);
        setPriceModal(true);
    };

    const updatePriceRow = (pr_id, patch) => {
        setPriceRows((prev) =>
            prev.map((r) => {
                if (r.pr_id !== pr_id) return r;
                const next = { ...r, ...patch };
                const isRev = String(next.tax_percent).startsWith('-');
                const driver = isRev ? next.total_selling_price : next.basic_price;
                const calc = calcTax(driver, next.tax_percent);
                return {
                    ...next,
                    basic_price: calc.basic_rate,
                    total_selling_price: calc.price,
                    tax_amount: calc.basic_tax_amount,
                    sgst_tax: calc.sgst_tax,
                    sgst_amount: calc.sgst_tax_amount,
                    cgst_tax: calc.cgst_tax,
                    cgst_amount: calc.cgst_tax_amount
                };
            })
        );
    };

    const applyPriceChange = async () => {
        setSaving(true);
        try {
            const payload = {
                effective_from: priceEff,
                data_transfer: 'pending',
                items: priceRows.map((r) => ({
                    pr_id: String(r.pr_id),
                    basic_price: Number(r.basic_price),
                    tax_percentage: Math.abs(parseFloat(r.tax_percent)) || 0,
                    tax_direction: String(r.tax_percent).startsWith('-') ? 'reverse' : 'forward',
                    tax_amount: Number(r.tax_amount),
                    sgst_tax: Number(r.sgst_tax),
                    sgst_amount: Number(r.sgst_amount),
                    cgst_tax: Number(r.cgst_tax),
                    cgst_amount: Number(r.cgst_amount),
                    total_selling_price: Number(r.total_selling_price)
                }))
            };
            await axios.post(`${API}bulk_price_change`, payload);
            toast.current?.show({ severity: 'success', summary: 'Price Updated', detail: `${priceRows.length} rows updated` });
            setPriceModal(false);
            setPriceConfirm(false);
            setSelectedRows([]);
            await loadData();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Price change failed' });
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================ */
    /* EXPORT                                                       */
    /* ============================================================ */
    const exportExcel = () => {
        const exportData = displayedProducts.map((row) => ({
            'Product Name': row.product_name || '',
            Branch: row.branch_name || '',
            Client: row.client_name || '',
            Category: row.category_name || '',
            'HSN Code': row.hsn_code || '',
            'Product Code': row.product_code || '',
            UOM: row.uom || '',
            'Basic Rate': row.basic_rate || 0,
            'Tax %': row.basic_tax_percent || 0,
            'Tax Direction': row.tax_direction === 'reverse' ? 'Reverse' : 'Forward',
            'Tax Amount': row.basic_tax_amount || 0,
            'SGST %': row.sgst_tax || 0,
            'SGST Amount': row.sgst_tax_amount || 0,
            'CGST %': row.cgst_tax || 0,
            'CGST Amount': row.cgst_tax_amount || 0,
            Price: row.price || 0,
            'Discount %': row.discount_perc || 0,
            'Token Group': row.tokengroup || '',
            'Product Type': row.product_type || '',
            'Sales Item Type': row.sales_item_type || '',
            'Effective From': fmtDMY(row.effective_from),
            Status: row.product_status || '',
            'Transfer Status': row.data_transfer || ''
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Products');
        XLSX.writeFile(wb, `Products_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.current?.show({ severity: 'success', summary: 'Exported', detail: `${displayedProducts.length} rows exported` });
    };

    /* ============================================================ */
    /* COLUMN BODY TEMPLATES                                        */
    /* ============================================================ */
    const statusBody = (r) => <Tag value={r.product_status} severity={r.product_status === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '72px', textAlign: 'center', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.3px' }} />;
    const priceBody = (r) => <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b', fontFamily: "'Inter', sans-serif" }}>₹{Number(r.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>;
    const discBody = (r) => {
        const d = r.discount_perc;
        if (!d || d === 0 || d === '0') return <span style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>—</span>;
        return <span style={{ background: '#fef3c7', color: '#b45309', padding: '0.1rem 0.4rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700', display: 'inline-block' }}>{d}% off</span>;
    };
    const tokenBody = (r) => (
        <div style={{ textAlign: 'center' }}>
            {r.token ? (
                <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#0369a1', fontWeight: '700', background: '#e0f2fe', padding: '0.1rem 0.4rem', borderRadius: '5px', marginBottom: '0.2rem', display: 'inline-block' }}>{r.token}</div>
            ) : (
                <div style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>—</div>
            )}
            {r.tokengroup ? (
                <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#4f46e5', fontWeight: '700', background: '#eef2ff', padding: '0.1rem 0.4rem', borderRadius: '5px', display: 'inline-block', marginTop: '0.2rem' }}>{r.tokengroup}</div>
            ) : (
                <div style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>—</div>
            )}
            <div style={{ marginTop: '0.2rem' }}>{discBody(r)}</div>
        </div>
    );
    // Combined: Effective From (top) + Sync Status (bottom)
    const dateTransferBody = (r) => {
        const v = (r.data_transfer || '').toLowerCase();
        const colorMap = { pending: '#d97706', added: '#16a34a', updated: '#2563eb', deleted: '#dc2626' };
        const syncColor = colorMap[v] || '#64748b';
        return (
            <div>
                <div style={{ fontWeight: '600', fontSize: '0.825rem', color: '#0f172a', lineHeight: 1.35, whiteSpace: 'nowrap' }}>{fmtDMY(r.effective_from) || '—'}</div>
                <div style={{ fontSize: '0.7rem', color: syncColor, marginTop: '0.15rem', fontWeight: '700', textTransform: 'capitalize' }}>{r.data_transfer || 'N/A'}</div>
            </div>
        );
    };

    // Combined: Price (top) + Status (bottom)
    const priceStatusBody = (r) => (
        <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.3, fontFamily: "'Inter', sans-serif" }}>₹{Number(r.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</div>
            <div style={{ marginTop: '0.25rem' }}>
                <Tag value={r.product_status} severity={r.product_status === 'Active' ? 'success' : 'danger'} rounded style={{ fontSize: '0.65rem', padding: '0.1rem 0.45rem', minWidth: 'unset' }} />
            </div>
        </div>
    );
    const productNameBody = (r) => (
        <div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '600', fontSize: '0.825rem', color: '#0f172a', lineHeight: 1.35 }}>{r.product_name}</div>
            {r.product_type && <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '0.1rem', fontWeight: '500' }}>{r.product_type}</div>}
        </div>
    );
    const categoryBody = (r) => <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: '0.8rem', color: '#6d28d9', fontWeight: '600' }}>{r.category_name || '—'}</span>;
    const truncate = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' };

    // Combined: Client (top) + Branch (bottom)
    const clientBranchBody = (r) => (
        <div>
            <div style={{ ...truncate, fontWeight: '600', fontSize: '0.825rem', color: '#0f172a', lineHeight: 1.35 }}>{r.client_name || '—'}</div>
            <div style={{ ...truncate, fontSize: '0.72rem', color: '#6d28d9', marginTop: '0.15rem', fontWeight: '600' }}>{r.branch_name || '—'}</div>
        </div>
    );
    const actionBody = (row) => <Button icon="pi pi-pencil" rounded text className="p-button-warning" onClick={() => openEdit(row)} tooltip="Edit" tooltipOptions={{ position: 'top' }} />;

    // Bulk actions (price/discount/status/token changes) apply to one client at a
    // time — once a row is selected, checkboxes for every other client's rows are
    // disabled so a mixed-client selection can never be built in the first place.
    const isRowSelectable = (options) => {
        if (!isGspos || !selectedRows.length) return true;
        return String(options.data.client_id) === String(selectedRows[0].client_id);
    };

    /* ============================================================ */
    /* RENDER                                                       */
    /* ============================================================ */
    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Page Header */}
            <div className="mb-4">
                <h2 style={{ fontSize: '1.75rem', fontWeight: '800', color: 'var(--primary-color)', letterSpacing: '-0.5px', margin: '0 0 0.25rem' }}>Product List</h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>View, manage and bulk-update products across clients and branches</p>
            </div>

            {/* Filter Card */}
            <Card className="mb-4 bill-reports-filter-card">
                <div className="flex flex-wrap gap-4 align-items-end bill-reports-filter-row">
                    {/* Client — gspos only; other roles are locked to their own client */}
                    {isGspos && (
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '200px' }}>
                            <label className="mb-2 font-medium text-sm">Client</label>
                            <Dropdown value={selectedClient} options={clients} onChange={(e) => setSelectedClient(e.value)} optionLabel="client_name" placeholder="Select Client" filter showClear className="w-full" />
                        </div>
                    )}
                    <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '200px' }}>
                        <label className="mb-2 font-medium text-sm">Branch</label>
                        <Dropdown
                            value={selectedBranch}
                            options={filteredBranches}
                            onChange={(e) => setSelectedBranch(e.value)}
                            optionLabel="branch_name"
                            placeholder={selectedClient ? 'Select Branch' : 'Select Client First'}
                            filter
                            showClear
                            disabled={!selectedClient}
                            className="w-full"
                        />
                    </div>
                    <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '200px' }}>
                        <label className="mb-2 font-medium text-sm">Category</label>
                        <Dropdown
                            value={selectedCategory}
                            options={filteredCategories}
                            onChange={(e) => setSelectedCategory(e.value)}
                            optionLabel="prcat_cat_name"
                            placeholder={selectedBranch ? 'Select Category' : 'Select Branch First'}
                            filter
                            showClear
                            disabled={!selectedBranch}
                            className="w-full"
                        />
                    </div>
                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Reset" icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} />
                    </div>
                </div>
            </Card>

            {/* ── Count Statistics Section ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                {[
                    { label: 'Total Products', count: totalRecords, icon: 'pi-th-large', color: '#4f46e5', lightBg: '#eef2ff', borderColor: '#c7d2fe', filterKey: 'all' },
                    { label: 'Active', count: activeCount, icon: 'pi-check-circle', color: '#16a34a', lightBg: '#f0fdf4', borderColor: '#bbf7d0', filterKey: 'active' },
                    { label: 'Inactive', count: inactiveCount, icon: 'pi-times-circle', color: '#dc2626', lightBg: '#fef2f2', borderColor: '#fecaca', filterKey: 'inactive' },
                    { label: 'Pending Sync', count: pendingCount, icon: 'pi-clock', color: '#d97706', lightBg: '#fffbeb', borderColor: '#fde68a', filterKey: 'pending' }
                ].map((s) => {
                    const active = statusFilter === s.filterKey;
                    return (
                        <div
                            key={s.label}
                            onClick={() => setStatusFilter(active ? 'active' : s.filterKey)}
                            style={{
                                background: active ? s.color : s.lightBg,
                                border: `1.5px solid ${active ? s.color : s.borderColor}`,
                                borderRadius: '12px',
                                padding: '1rem 1.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.875rem',
                                cursor: 'pointer',
                                transition: 'all 0.18s ease',
                                boxShadow: active ? `0 4px 16px ${s.color}40` : '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                        >
                            <div style={{ width: '46px', height: '46px', borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? 'rgba(255,255,255,0.22)' : s.color }}>
                                <i className={`pi ${s.icon}`} style={{ fontSize: '1.25rem', color: '#fff' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '1.8rem', fontWeight: '800', lineHeight: 1, color: active ? '#fff' : s.color, fontFamily: "'Inter', sans-serif" }}>{s.count.toLocaleString()}</div>
                                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: active ? 'rgba(255,255,255,0.82)' : '#64748b', marginTop: '0.2rem', letterSpacing: '0.2px' }}>{s.label}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Table Card */}
            <Card className="bill-reports-container">
                {/* ── Row 1: Title + Toolbar ── */}
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <div className="flex align-items-center gap-2">
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.3px' }}>Products</h3>
                        <span style={{ background: '#f1f5f9', color: '#475569', padding: '0.2rem 0.65rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.2px' }}>{displayedProducts.length} records</span>
                        {selectedRows.length > 0 && (
                            <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '0.2rem 0.65rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700' }}>
                                <i className="pi pi-check-square mr-1" style={{ fontSize: '0.7rem' }} />
                                {selectedRows.length} selected
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2 align-items-center flex-wrap">
                        <span className="p-input-icon-left" style={{ minWidth: '240px' }}>
                            <i className="pi pi-search" style={{ color: '#94a3b8' }} />
                            <InputText
                                value={globalFilterValue}
                                onChange={(e) => setGlobalFilterValue(e.target.value)}
                                placeholder="Search product, branch, category…"
                                className="w-full"
                                style={{ borderRadius: '8px', fontSize: '0.875rem' }}
                            />
                        </span>
                        <Button
                            label="Import"
                            icon="pi pi-upload"
                            className="p-button-warning p-button-outlined"
                            onClick={() => {
                                resetImportForm();
                                setImportModal(true);
                            }}
                        />
                        <Button label="Export" icon="pi pi-download" className="p-button-secondary p-button-outlined" onClick={exportExcel} />
                        <Menu
                            model={[
                                { label: 'Add Product', icon: 'pi pi-plus', command: openAdd },
                                { separator: true },
                                { label: 'Change Price', icon: 'pi pi-dollar', disabled: !selectedRows.length, command: openPriceChange },
                                {
                                    label: 'Token Group',
                                    icon: 'pi pi-tag',
                                    disabled: !selectedRows.length,
                                    command: () => {
                                        setTokenValue('');
                                        setTokenEff(toISODate(new Date()));
                                        setTokenConfirm(false);
                                        setTokenModal(true);
                                    }
                                },
                                { label: 'Set Active', icon: 'pi pi-check-circle', disabled: !selectedRows.length, command: () => confirmStatusUpdate('Active') },
                                { label: 'Set Inactive', icon: 'pi pi-times-circle', disabled: !selectedRows.length, command: () => confirmStatusUpdate('Inactive') },
                                {
                                    label: 'Apply Discount',
                                    icon: 'pi pi-percentage',
                                    disabled: !selectedRows.length,
                                    command: () => {
                                        setDiscValue(0);
                                        setDiscEff(toISODate(new Date()));
                                        setDiscConfirm(false);
                                        setDiscModal(true);
                                    }
                                },
                                { label: 'Promote to Live', icon: 'pi pi-send', disabled: !selectedRows.length || saving, command: promoteToLive }
                            ]}
                            popup
                            ref={actionsMenu}
                            id="actions-menu"
                        />
                        <Button label="Actions" icon="pi pi-chevron-down" iconPos="right" className="p-button-primary" onClick={(e) => actionsMenu.current.toggle(e)} aria-controls="actions-menu" aria-haspopup />
                    </div>
                </div>

                {/* ── DataTable ── */}
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <DataTable
                        ref={dt}
                        value={displayedProducts}
                        selection={isGspos ? selectedRows : selectedRows[0] || null}
                        onSelectionChange={(e) => setSelectedRows(isGspos ? e.value || [] : e.value ? [e.value] : [])}
                        isDataSelectable={isRowSelectable}
                        rowClassName={(row) => (isRowSelectable({ data: row }) ? '' : 'p-disabled')}
                        dataKey="pr_id"
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                        currentPageReportTemplate="{first}–{last} of {totalRecords}"
                        showGridlines
                        tableStyle={{ width: '100%', tableLayout: 'fixed', fontSize: '0.85rem' }}
                        className="p-datatable-sm"
                        emptyMessage={
                            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#94a3b8' }}>
                                <i className="pi pi-inbox" style={{ fontSize: '2.25rem', display: 'block', marginBottom: '0.75rem' }} />
                                <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>No products found</p>
                                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>Try adjusting your filters or search term</p>
                            </div>
                        }
                        pt={{
                            headerRow: { style: { background: '#f8fafc' } },
                            bodyRow: { style: { verticalAlign: 'middle' } }
                        }}
                    >
                        <Column selectionMode={isGspos ? 'multiple' : 'single'} style={{ width: '3rem', textAlign: 'center' }} headerStyle={{ width: '3rem', background: '#f8fafc', padding: '0.5rem 0' }} bodyStyle={{ padding: '0.5rem 0', verticalAlign: 'middle' }} />

                        <Column
                            field="client_name"
                            header="Client / Branch"
                            body={clientBranchBody}
                            sortable
                            style={{ width: '17%' }}
                            headerStyle={{ textAlign: 'left' }}
                            bodyStyle={{ textAlign: 'left', verticalAlign: 'middle', padding: '0.5rem 0.75rem', overflow: 'hidden' }}
                        />

                        <Column
                            field="product_name"
                            header="Product"
                            body={productNameBody}
                            sortable
                            style={{ width: '20%' }}
                            headerStyle={{ textAlign: 'left' }}
                            bodyStyle={{ textAlign: 'left', verticalAlign: 'middle', padding: '0.5rem 0.75rem', overflow: 'hidden' }}
                        />

                        <Column
                            field="category_name"
                            header="Category"
                            body={categoryBody}
                            sortable
                            style={{ width: '15%' }}
                            headerStyle={{ textAlign: 'left' }}
                            bodyStyle={{ textAlign: 'left', verticalAlign: 'middle', padding: '0.5rem 0.75rem', overflow: 'hidden' }}
                        />

                        <Column
                            field="price"
                            header="Price / Status"
                            body={priceStatusBody}
                            sortable
                            style={{ width: '13%' }}
                            headerStyle={{ textAlign: 'center' }}
                            bodyStyle={{ textAlign: 'center', verticalAlign: 'middle', padding: '0.5rem 0.5rem' }}
                        />

                        <Column field="tokengroup" header="Token / Group / Discount" body={tokenBody} style={{ width: '15%' }} headerStyle={{ textAlign: 'center' }} bodyStyle={{ textAlign: 'center', verticalAlign: 'middle', padding: '0.5rem 0.5rem' }} />

                        <Column
                            field="effective_from"
                            header="Date / Sync"
                            body={dateTransferBody}
                            sortable
                            style={{ width: '20%' }}
                            headerStyle={{ textAlign: 'left' }}
                            bodyStyle={{ textAlign: 'left', verticalAlign: 'middle', padding: '0.5rem 0.75rem', overflow: 'hidden' }}
                        />

                        <Column
                            header="Actions"
                            body={actionBody}
                            style={{ width: '4rem' }}
                            headerStyle={{ textAlign: 'center', background: '#f8fafc' }}
                            bodyStyle={{ textAlign: 'center', verticalAlign: 'middle', padding: '0.5rem 0' }}
                        />
                    </DataTable>
                )}
            </Card>

            {/* ======= ADD / EDIT DIALOG ======= */}
            <Dialog
                visible={editModal}
                onHide={() => setEditModal(false)}
                style={{ width: '90vw', maxWidth: '1080px' }}
                breakpoints={{ '1400px': '92vw', '960px': '95vw', '640px': '98vw' }}
                contentStyle={{ maxHeight: '72vh', overflowY: 'auto', padding: '1.25rem' }}
                position="center"
                modal
                header={
                    <div className="dialog-header-custom">
                        <i className="pi pi-shopping-bag header-icon"></i>
                        <span>{isEditing ? 'Edit Product' : 'New Product'}</span>
                    </div>
                }
                className="category-dialog compact-dialog"
                footer={
                    <div className="dialog-footer">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-secondary cancel-btn" onClick={() => setEditModal(false)} />
                        <Button label={saving ? 'Saving...' : isEditing ? 'Update' : 'Save'} icon="pi pi-check" className="p-button-raised save-btn" onClick={saveProduct} disabled={saving} />
                    </div>
                }
            >
                <div style={{ marginBottom: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#64748b', fontSize: '0.875rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Linkage</h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                    {/* Client — gspos only; other roles are locked to their own client */}
                    {isGspos && (
                        <Field label="Client" required error={errors.client_id}>
                            <Dropdown
                                value={form.client_id}
                                options={clients.map((c) => ({ label: c.client_name, value: c.client_id }))}
                                onChange={(e) => setFormField({ client_id: e.value, branch_id: null, category_master_id: null, prcat_cat_id: null })}
                                placeholder="Select Client"
                                filter
                                style={{ width: '100%' }}
                            />
                        </Field>
                    )}
                    <Field label="Branch" required error={errors.branch_id}>
                        <Dropdown
                            value={form.branch_id}
                            options={mBranches.map((b) => ({ label: b.branch_name, value: b.branch_id }))}
                            onChange={(e) => setFormField({ branch_id: e.value, category_master_id: null, prcat_cat_id: null })}
                            placeholder={form.client_id ? 'Select Branch' : 'Select Client first'}
                            disabled={!form.client_id}
                            filter
                            style={{ width: '100%' }}
                        />
                    </Field>
                    <Field label="Category Master" required error={errors.category_master_id}>
                        <Dropdown
                            value={form.category_master_id}
                            options={mCatMasters.map((c) => ({ label: c.category_master_name || c.cat_master_name || `#${c.category_master_id}`, value: c.category_master_id }))}
                            onChange={(e) => {
                                const cm = mCatMasters.find((c) => String(c.category_master_id) === String(e.value));
                                const cmName = String(cm?.category_master_name || cm?.cat_master_name || '')
                                    .trim()
                                    .toLowerCase();
                                const branchCats = categories.filter((c) => String(c.branch_id) === String(form.branch_id));
                                // Product Category dropdown is hidden — auto-pick the category linked
                                // to the selected Category Master (same branch), falling back to a
                                // name match for category rows that don't carry that link.
                                const matched = branchCats.find((c) => String(c.category_master_id || c.catmaster_id || c.cat_master_id) === String(e.value))
                                    || branchCats.find((c) => String(c.prcat_cat_name || '').trim().toLowerCase() === cmName);
                                setFormField({ category_master_id: e.value, prcat_cat_id: matched ? matched.prcat_cat_id : null });
                            }}
                            placeholder={form.branch_id ? 'Select Category Master' : 'Select Branch first'}
                            disabled={!form.branch_id}
                            filter
                            style={{ width: '100%' }}
                        />
                    </Field>
                    {/* Product Category / Platform are normally auto-derived and hidden (see Category
                        Master onChange and openAdd above) — these only appear as a manual escape
                        hatch when that auto-match comes up empty, so the required field never gets
                        silently stuck with no way to fix it. */}
                    {form.category_master_id && !form.prcat_cat_id && (
                        <Field label="Product Category" required error={errors.prcat_cat_id}>
                            <Dropdown
                                value={form.prcat_cat_id}
                                options={categoriesForBranch.map((c) => ({ label: c.prcat_cat_name, value: c.prcat_cat_id }))}
                                onChange={(e) => setFormField({ prcat_cat_id: e.value })}
                                placeholder="Couldn't auto-match — select Product Category"
                                filter
                                style={{ width: '100%' }}
                            />
                        </Field>
                    )}
                    {!form.platform_id && (
                        <Field label="Platform" required error={errors.platform_id}>
                            <Dropdown
                                value={form.platform_id}
                                options={platforms.map((p) => ({ label: p.platform_name, value: p.platform_id }))}
                                onChange={(e) => setFormField({ platform_id: e.value })}
                                placeholder="Couldn't default to POS — select Platform"
                                filter
                                style={{ width: '100%' }}
                            />
                        </Field>
                    )}
                </div>

                <div style={{ marginBottom: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#64748b', fontSize: '0.875rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pricing</h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                    <Field label="Product Name" required error={errors.product_name}>
                        <InputText value={form.product_name} onChange={(e) => setFormField({ product_name: e.target.value })} style={{ width: '100%' }} />
                    </Field>
                    <Field label="UOM" required error={errors.uom}>
                        <InputText value={form.uom} onChange={(e) => setFormField({ uom: e.target.value })} style={{ width: '100%' }} />
                    </Field>
                    <Field label="Tax % (+Forward / −Reverse)" required error={errors.basic_tax_percent}>
                        <Dropdown value={form.basic_tax_percent} options={TAX_OPTIONS} onChange={(e) => setFormField({ basic_tax_percent: e.value })} style={{ width: '100%' }} />
                    </Field>
                    <Field label={String(form.basic_tax_percent).startsWith('-') ? 'Basic Rate (auto)' : 'Basic Rate'}>
                        <InputNumber
                            value={form.basic_rate}
                            onValueChange={(e) => setFormField({ basic_rate: e.value })}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={3}
                            disabled={String(form.basic_tax_percent).startsWith('-')}
                            style={{ width: '100%' }}
                        />
                    </Field>
                    <Field label={String(form.basic_tax_percent).startsWith('-') ? 'Selling Price' : 'Selling Price (auto)'} required error={errors.price}>
                        <InputNumber
                            value={form.price}
                            onValueChange={(e) => setFormField({ price: e.value })}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            disabled={!String(form.basic_tax_percent).startsWith('-')}
                            style={{ width: '100%' }}
                        />
                    </Field>
                    <Field label="Tax Amount (auto)">
                        <InputNumber value={form.basic_tax_amount} disabled mode="decimal" minFractionDigits={0} maxFractionDigits={3} style={{ width: '100%' }} />
                    </Field>
                    <Field label="Effective From" required error={errors.effective_from}>
                        <Calendar value={parseISODate(form.effective_from)} onChange={(e) => setFormField({ effective_from: toISODate(e.value) })} dateFormat="dd-mm-yy" showIcon style={{ width: '100%' }} />
                    </Field>
                    <Field label="Status" required error={errors.product_status}>
                        <Dropdown value={form.product_status} options={STATUS_OPTIONS} onChange={(e) => setFormField({ product_status: e.value })} style={{ width: '100%' }} />
                    </Field>
                    <Field label="Discount %">
                        <Dropdown value={form.discount_perc} options={DISCOUNT_OPTIONS} onChange={(e) => setFormField({ discount_perc: e.value })} showClear placeholder="No Discount" style={{ width: '100%' }} />
                    </Field>
                </div>

                <details style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: '600', color: '#475569', fontSize: '0.875rem' }}>
                        <i className="pi pi-chevron-down" style={{ marginRight: '0.5rem' }} /> Optional Details
                    </summary>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                        <Field label="HSN Code">
                            <InputText value={form.hsn_code} onChange={(e) => setFormField({ hsn_code: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Product No">
                            <InputText value={form.product_no} onChange={(e) => setFormField({ product_no: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Product Code">
                            <InputText value={form.product_code} onChange={(e) => setFormField({ product_code: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Sorting Order">
                            <InputText value={form.sorting_order} onChange={(e) => setFormField({ sorting_order: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Stock">
                            <InputText value={form.product_stock} onChange={(e) => setFormField({ product_stock: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Token">
                            <InputText value={form.token} onChange={(e) => setFormField({ token: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Token Group">
                            <InputText value={form.tokengroup} onChange={(e) => setFormField({ tokengroup: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Sales Item Type">
                            <Dropdown value={form.sales_item_type} options={SALES_ITEM_TYPE_OPTIONS} onChange={(e) => setFormField({ sales_item_type: e.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Product Type">
                            <Dropdown value={form.product_type} options={PRODUCT_TYPE_OPTIONS} onChange={(e) => setFormField({ product_type: e.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Product Image">
                            <InputText value={form.product_image} onChange={(e) => setFormField({ product_image: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Image Path">
                            <InputText value={form.product_image_path} onChange={(e) => setFormField({ product_image_path: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                        <Field label="Barcode">
                            <InputText value={form.barcode_number} onChange={(e) => setFormField({ barcode_number: e.target.value })} style={{ width: '100%' }} />
                        </Field>
                    </div>
                </details>
            </Dialog>

            {/* ======= BULK TOKEN DIALOG ======= */}
            <Dialog
                visible={tokenModal}
                onHide={() => {
                    setTokenModal(false);
                    setTokenConfirm(false);
                }}
                style={{ width: '90vw', maxWidth: '520px' }}
                breakpoints={{ '960px': '92vw', '640px': '98vw' }}
                contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                position="center"
                modal
                header={
                    <div className="dialog-header-custom">
                        <i className="pi pi-tag header-icon"></i>
                        <span>Token Group</span>
                    </div>
                }
                className="category-dialog compact-dialog"
                footer={
                    <div className="dialog-footer">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary cancel-btn"
                            onClick={() => {
                                setTokenModal(false);
                                setTokenConfirm(false);
                            }}
                        />
                        {!tokenConfirm ? (
                            <Button label="Next" icon="pi pi-arrow-right" className="p-button-raised save-btn" onClick={() => setTokenConfirm(true)} />
                        ) : (
                            <Button label={saving ? 'Applying...' : 'Confirm & Apply'} icon="pi pi-check" className="p-button-raised save-btn" disabled={saving} onClick={applyBulkToken} />
                        )}
                    </div>
                }
            >
                <div className="dialog-content">
                    <div style={{ padding: '0.75rem 1rem', background: '#fef3c7', borderRadius: '8px', marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <i className="pi pi-info-circle" style={{ color: '#d97706' }} />
                        <span>
                            Apply token group to <strong>{selectedRows.length}</strong> selected product(s).
                        </span>
                    </div>
                    <Field label="Token Group" required>
                        <InputText value={tokenValue} onChange={(e) => setTokenValue(e.target.value)} placeholder="Enter token number / group" style={{ width: '100%' }} />
                    </Field>
                    <Field label="Effective From" required>
                        <Calendar value={parseISODate(tokenEff)} onChange={(e) => setTokenEff(toISODate(e.value))} dateFormat="dd-mm-yy" showIcon style={{ width: '100%' }} />
                    </Field>
                    {tokenConfirm && (
                        <div style={{ padding: '0.75rem 1rem', background: '#fee2e2', borderRadius: '8px', marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626' }} />
                            <span>
                                Set token group to <strong>"{tokenValue}"</strong> for <strong>{selectedRows.length}</strong> product(s), effective <strong>{fmtDMY(tokenEff)}</strong>?
                            </span>
                        </div>
                    )}
                </div>
            </Dialog>

            {/* ======= BULK DISCOUNT DIALOG ======= */}
            <Dialog
                visible={discModal}
                onHide={() => {
                    setDiscModal(false);
                    setDiscConfirm(false);
                }}
                style={{ width: '90vw', maxWidth: '520px' }}
                breakpoints={{ '960px': '92vw', '640px': '98vw' }}
                contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                position="center"
                modal
                header={
                    <div className="dialog-header-custom">
                        <i className="pi pi-percentage header-icon"></i>
                        <span>Change Discount %</span>
                    </div>
                }
                className="category-dialog compact-dialog"
                footer={
                    <div className="dialog-footer">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary cancel-btn"
                            onClick={() => {
                                setDiscModal(false);
                                setDiscConfirm(false);
                            }}
                        />
                        {!discConfirm ? (
                            <Button label="Next" icon="pi pi-arrow-right" className="p-button-raised save-btn" onClick={() => setDiscConfirm(true)} />
                        ) : (
                            <Button label={saving ? 'Applying...' : 'Confirm & Apply'} icon="pi pi-check" className="p-button-raised save-btn" disabled={saving} onClick={applyBulkDiscount} />
                        )}
                    </div>
                }
            >
                <div className="dialog-content">
                    <div style={{ padding: '0.75rem 1rem', background: '#fef3c7', borderRadius: '8px', marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <i className="pi pi-info-circle" style={{ color: '#d97706' }} />
                        <span>
                            Apply discount to <strong>{selectedRows.length}</strong> selected product(s).
                        </span>
                    </div>
                    <Field label="Discount %" required>
                        <Dropdown value={discValue} options={DISCOUNT_OPTIONS} onChange={(e) => setDiscValue(e.value)} placeholder="Select discount" style={{ width: '100%' }} />
                    </Field>
                    <Field label="Effective From" required>
                        <Calendar value={parseISODate(discEff)} onChange={(e) => setDiscEff(toISODate(e.value))} dateFormat="dd-mm-yy" showIcon style={{ width: '100%' }} />
                    </Field>
                    {discConfirm && (
                        <div style={{ padding: '0.75rem 1rem', background: '#fee2e2', borderRadius: '8px', marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626' }} />
                            <span>
                                Apply <strong>{discValue}% discount</strong> to <strong>{selectedRows.length}</strong> product(s), effective <strong>{fmtDMY(discEff)}</strong>?
                            </span>
                        </div>
                    )}
                </div>
            </Dialog>

            {/* ======= BULK PRICE CHANGE DIALOG ======= */}
            <Dialog
                visible={priceModal}
                onHide={() => {
                    setPriceModal(false);
                    setPriceConfirm(false);
                }}
                style={{ width: '96vw', maxWidth: '1200px' }}
                breakpoints={{ '1400px': '97vw', '960px': '98vw', '640px': '99vw' }}
                contentStyle={{ maxHeight: '78vh', overflowY: 'auto' }}
                position="center"
                modal
                header={
                    <div className="dialog-header-custom">
                        <i className="pi pi-dollar header-icon"></i>
                        <span>
                            Change Price ({priceRows.length} item{priceRows.length !== 1 ? 's' : ''})
                        </span>
                    </div>
                }
                className="category-dialog compact-dialog"
                footer={
                    <div className="dialog-footer">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary cancel-btn"
                            onClick={() => {
                                setPriceModal(false);
                                setPriceConfirm(false);
                            }}
                        />
                        {!priceConfirm ? (
                            <Button label="Next" icon="pi pi-arrow-right" className="p-button-raised save-btn" onClick={() => setPriceConfirm(true)} />
                        ) : (
                            <Button label={saving ? 'Applying...' : 'Confirm & Apply'} icon="pi pi-check" className="p-button-raised save-btn" disabled={saving} onClick={applyPriceChange} />
                        )}
                    </div>
                }
            >
                <div className="dialog-content">
                    <div style={{ padding: '0.75rem 1rem', background: '#fef3c7', borderRadius: '8px', marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <i className="pi pi-info-circle" style={{ color: '#d97706' }} />
                        <span>
                            Review and edit prices for <strong>{priceRows.length}</strong> product(s). Each row recalculates independently.
                        </span>
                    </div>
                    <div style={{ maxWidth: 360, marginBottom: '1rem' }}>
                        <Field label="Effective From" required>
                            <Calendar value={parseISODate(priceEff)} onChange={(e) => setPriceEff(toISODate(e.value))} dateFormat="dd-mm-yy" showIcon style={{ width: '100%' }} />
                        </Field>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                <tr>
                                    {['Branch', 'Item', 'Basic', 'Tax %', 'Tax Amt', 'SGST %', 'SGST Amt', 'CGST %', 'CGST Amt', 'Selling'].map((h) => (
                                        <th key={h} style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e2e8f0' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {priceRows.map((r) => {
                                    const isRev = String(r.tax_percent).startsWith('-');
                                    return (
                                        <tr key={r.pr_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '0.75rem' }}>{r.branch_name}</td>
                                            <td style={{ padding: '0.75rem' }}>{r.product_name}</td>
                                            <td style={{ padding: '0.75rem' }}>
                                                <InputNumber
                                                    value={r.basic_price}
                                                    onValueChange={(e) => updatePriceRow(r.pr_id, { basic_price: toNum(e.value) })}
                                                    mode="decimal"
                                                    minFractionDigits={0}
                                                    maxFractionDigits={3}
                                                    inputStyle={{ width: 100 }}
                                                    disabled={isRev}
                                                />
                                            </td>
                                            <td style={{ padding: '0.75rem' }}>
                                                <Dropdown value={r.tax_percent} options={TAX_OPTIONS} onChange={(e) => updatePriceRow(r.pr_id, { tax_percent: e.value })} style={{ width: 140 }} />
                                            </td>
                                            <td style={{ padding: '0.75rem' }}>{r.tax_amount}</td>
                                            <td style={{ padding: '0.75rem' }}>{r.sgst_tax}</td>
                                            <td style={{ padding: '0.75rem' }}>{r.sgst_amount}</td>
                                            <td style={{ padding: '0.75rem' }}>{r.cgst_tax}</td>
                                            <td style={{ padding: '0.75rem' }}>{r.cgst_amount}</td>
                                            <td style={{ padding: '0.75rem' }}>
                                                <InputNumber
                                                    value={r.total_selling_price}
                                                    onValueChange={(e) => updatePriceRow(r.pr_id, { total_selling_price: toNum(e.value) })}
                                                    mode="decimal"
                                                    minFractionDigits={0}
                                                    maxFractionDigits={2}
                                                    inputStyle={{ width: 100 }}
                                                    disabled={!isRev}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {priceConfirm && (
                        <div style={{ padding: '0.75rem 1rem', background: '#fee2e2', borderRadius: '8px', marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626' }} />
                            <span>
                                Confirm updating price for <strong>{priceRows.length}</strong> row(s) effective <strong>{fmtDMY(priceEff)}</strong>?
                            </span>
                        </div>
                    )}
                </div>
            </Dialog>

            {/* ======= IMPORT DIALOG ======= */}
            <Dialog
                visible={importModal}
                onHide={() => {
                    resetImportForm();
                    setImportModal(false);
                }}
                style={{ width: '90vw', maxWidth: '700px' }}
                breakpoints={{ '960px': '93vw', '640px': '98vw' }}
                contentStyle={{ maxHeight: '78vh', overflowY: 'auto' }}
                position="center"
                modal
                header={
                    <div className="dialog-header-custom">
                        <i className="pi pi-upload header-icon"></i>
                        <span>Import Products</span>
                    </div>
                }
                className="category-dialog compact-dialog"
                footer={
                    <div className="dialog-footer">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary cancel-btn"
                            onClick={() => {
                                resetImportForm();
                                setImportModal(false);
                            }}
                        />
                        <Button label={savingImport ? 'Uploading...' : 'Upload'} icon="pi pi-cloud-upload" className="p-button-raised save-btn" onClick={handleImportSubmit} disabled={savingImport} />
                    </div>
                }
            >
                <div className="dialog-content">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                        <Field label="Client" required>
                            <Dropdown
                                value={importForm.client}
                                options={clients}
                                onChange={(e) => setImportForm({ client: e.value, branch: null, catMaster: null, category: null, platform: null, section: null })}
                                optionLabel="client_name"
                                placeholder="Select Client"
                                filter
                                style={{ width: '100%' }}
                            />
                        </Field>
                        <Field label="Branch">
                            <Dropdown
                                value={importForm.branch}
                                options={importBranches}
                                onChange={(e) => setImportForm({ ...importForm, branch: e.value })}
                                optionLabel="branch_name"
                                placeholder="Select Branch (optional)"
                                disabled={!importForm.client}
                                filter
                                showClear
                                style={{ width: '100%' }}
                            />
                        </Field>
                        <Field label="Category Master" required>
                            <Dropdown
                                value={importForm.catMaster}
                                options={importCatMasters}
                                onChange={(e) => setImportForm({ ...importForm, catMaster: e.value, category: null })}
                                optionLabel={(o) => o.category_master_name || o.cat_master_name || `#${o.category_master_id}`}
                                placeholder={importForm.client ? 'Select Category Master' : 'Select Client first'}
                                disabled={!importForm.client}
                                filter
                                style={{ width: '100%' }}
                            />
                        </Field>
                        <Field label="Product Category" required>
                            <Dropdown
                                value={importForm.category}
                                options={importCategories}
                                onChange={(e) => setImportForm({ ...importForm, category: e.value })}
                                optionLabel="prcat_cat_name"
                                optionValue="prcat_cat_id"
                                placeholder={importForm.catMaster ? 'Select Product Category' : 'Select Category Master first'}
                                disabled={!importForm.catMaster}
                                filter
                                style={{ width: '100%' }}
                            />
                        </Field>
                        <Field label="Platform" required>
                            <Dropdown value={importForm.platform} options={platforms} onChange={(e) => setImportForm({ ...importForm, platform: e.value })} optionLabel="platform_name" placeholder="Select Platform" filter style={{ width: '100%' }} />
                        </Field>
                        <Field label="Section (Optional)">
                            <Dropdown
                                value={importForm.section}
                                options={sections}
                                onChange={(e) => setImportForm({ ...importForm, section: e.value })}
                                optionLabel="section_name"
                                placeholder="Select Section"
                                filter
                                showClear
                                style={{ width: '100%' }}
                            />
                        </Field>
                    </div>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} style={{ marginBottom: '1rem', display: 'block' }} />
                    <textarea
                        placeholder="Or paste Excel data here (Ctrl+V)"
                        onPaste={handleImportPaste}
                        style={{ width: '100%', minHeight: '100px', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.875rem', boxSizing: 'border-box' }}
                    />
                    {!!importRows.length && (
                        <div style={{ marginTop: '1rem' }}>
                            <div style={{ padding: '0.75rem 1rem', background: '#dcfce7', borderRadius: '8px', marginBottom: '1rem', fontWeight: '600', color: '#065f46' }}>{importRows.length} product rows ready</div>
                            <div style={{ overflowX: 'auto', maxHeight: '250px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.813rem' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                                        <tr>
                                            {['Product', 'HSN', 'Code', 'UOM', 'Rate', 'Tax%', 'Selling'].map((h) => (
                                                <th key={h} style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e2e8f0' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importRows.map((r, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '0.75rem' }}>{r.product_name}</td>
                                                <td style={{ padding: '0.75rem' }}>{r.hsn_code}</td>
                                                <td style={{ padding: '0.75rem' }}>{r.product_code}</td>
                                                <td style={{ padding: '0.75rem' }}>{r.uom}</td>
                                                <td style={{ padding: '0.75rem' }}>{r.basic_rate}</td>
                                                <td style={{ padding: '0.75rem' }}>{r.tax_percentage}</td>
                                                <td style={{ padding: '0.75rem' }}>{r.selling_price}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
};

export default ProductList;
